import type { Dispatch, SetStateAction } from "react";
import type { NavigateFunction } from "react-router-dom";
import type { SpeechResult } from "@/features/companion";
import { enqueueCursorMove } from "../../services/marcusCursorQueue";
import {
  routeWithWorkspace,
  STATIC_ADCLONE_OPEN_DELAY_MS,
  STATIC_ADCLONE_PATH,
  STATIC_ADCLONE_TARGET_TIMEOUT_MS,
  waitForHighlightTarget,
} from "./marcusOnboardingFlow";
import {
  ONBOARDING_STATIC_ADCLONE_AI_ADD_COPY,
  ONBOARDING_STATIC_ADCLONE_SEARCH_COPY,
  STATIC_ADCLONE_DISCOVER_BUTTON_TARGET,
  STATIC_ADCLONE_DISCOVERED_ADS_TARGET,
  STATIC_ADCLONE_PRODUCT_AI_ADD_TARGET,
  STATIC_ADCLONE_PRODUCT_IMAGE_TARGET,
  STATIC_ADCLONE_PRODUCT_PICKER_TARGET,
  STATIC_ADS_CLONE_TARGET,
} from "./marcusOnboardingTargets";

export interface StaticAdcloneChoreoContext {
  isCurrentRun: () => boolean;
  navigate: NavigateFunction;
  revealNextGate: () => void;
  setSpotlightTarget: Dispatch<SetStateAction<string | null>>;
  speakOnboardingHighlightAndWait: (
    comment: string | undefined,
    fallbackMs: number,
  ) => Promise<SpeechResult>;
}

function highlightSelector(target: string): string {
  const escaped =
    typeof CSS !== "undefined" && typeof CSS.escape === "function"
      ? CSS.escape(target)
      : target.replace(/"/g, '\\"');
  return `[data-highlight-id="${escaped}"]`;
}

function findHighlightTarget(target: string): HTMLElement | null {
  const el = document.querySelector(highlightSelector(target));
  return el instanceof HTMLElement ? el : null;
}

function isTargetDisabled(el: HTMLElement): boolean {
  if (
    (el instanceof HTMLButtonElement ||
      el instanceof HTMLInputElement ||
      el instanceof HTMLSelectElement ||
      el instanceof HTMLTextAreaElement) &&
    el.disabled
  ) {
    return true;
  }
  return el.getAttribute("aria-disabled") === "true";
}

async function waitForInteractiveHighlightTarget(
  target: string,
  timeoutMs: number,
): Promise<HTMLElement | null> {
  if (typeof document === "undefined") return null;
  const started = Date.now();
  while (Date.now() - started < timeoutMs) {
    const el = findHighlightTarget(target);
    if (el && !isTargetDisabled(el)) return el;
    await new Promise((resolve) => window.setTimeout(resolve, 100));
  }
  return null;
}

function phantomPress(el: HTMLElement): void {
  el.classList.add("saga-phantom-press");
  window.setTimeout(() => el.classList.remove("saga-phantom-press"), 520);
  el.click();
}

export function isStaticAdcloneProductAiAddReady(): boolean {
  const addBtn = findHighlightTarget(STATIC_ADCLONE_PRODUCT_AI_ADD_TARGET);
  return Boolean(addBtn && !isTargetDisabled(addBtn));
}

/**
 * Runs after narration finishes for each static_adclone cursor target.
 * Returns true if the target was handled.
 */
export function handleStaticAdcloneCursorArrival(
  target: string,
  ctx: StaticAdcloneChoreoContext,
): boolean {
  if (target === STATIC_ADS_CLONE_TARGET) {
    window.setTimeout(() => {
      ctx.navigate(routeWithWorkspace(STATIC_ADCLONE_PATH));
      void waitForHighlightTarget(
        STATIC_ADCLONE_PRODUCT_PICKER_TARGET,
        STATIC_ADCLONE_TARGET_TIMEOUT_MS,
      ).then((found) => {
        if (!ctx.isCurrentRun()) return;
        if (!found) {
          ctx.revealNextGate();
          return;
        }
        enqueueCursorMove({
          target: STATIC_ADCLONE_PRODUCT_PICKER_TARGET,
          pulses: 5,
          source: "onboarding",
        });
      });
    }, STATIC_ADCLONE_OPEN_DELAY_MS);
    return true;
  }

  if (target === STATIC_ADCLONE_PRODUCT_PICKER_TARGET) {
    const addBtn = findHighlightTarget(STATIC_ADCLONE_PRODUCT_PICKER_TARGET);
    if (addBtn) phantomPress(addBtn);
    void waitForHighlightTarget(
      STATIC_ADCLONE_PRODUCT_IMAGE_TARGET,
      STATIC_ADCLONE_TARGET_TIMEOUT_MS,
    ).then((found) => {
      if (!ctx.isCurrentRun()) return;
      if (!found) {
        ctx.revealNextGate();
        return;
      }
      enqueueCursorMove({
        target: STATIC_ADCLONE_PRODUCT_IMAGE_TARGET,
        pulses: 5,
        source: "onboarding",
      });
    });
    return true;
  }

  if (target === STATIC_ADCLONE_PRODUCT_AI_ADD_TARGET) {
    const addBtn = findHighlightTarget(STATIC_ADCLONE_PRODUCT_AI_ADD_TARGET);
    if (addBtn && !isTargetDisabled(addBtn)) phantomPress(addBtn);
    return true;
  }

  if (target === STATIC_ADCLONE_DISCOVER_BUTTON_TARGET) {
    const discoverBtn = findHighlightTarget(STATIC_ADCLONE_DISCOVER_BUTTON_TARGET);
    if (discoverBtn && !isTargetDisabled(discoverBtn)) phantomPress(discoverBtn);
    void waitForHighlightTarget(STATIC_ADCLONE_DISCOVERED_ADS_TARGET, 60_000).then(
      (found) => {
        if (!ctx.isCurrentRun()) return;
        if (!found) {
          ctx.revealNextGate();
          return;
        }
        enqueueCursorMove({
          target: STATIC_ADCLONE_DISCOVERED_ADS_TARGET,
          pulses: 5,
          source: "onboarding",
        });
      },
    );
    return true;
  }

  return false;
}

/**
 * After the user stages a product image, queue the cursor to the in-form
 * Add Product CTA that starts the Gemini extraction and save flow.
 */
export async function runStaticAdcloneAiAddStep(
  ctx: StaticAdcloneChoreoContext,
): Promise<void> {
  if (!ctx.isCurrentRun()) return;
  ctx.setSpotlightTarget((current) =>
    current === STATIC_ADCLONE_PRODUCT_IMAGE_TARGET ? null : current,
  );
  const addBtn = await waitForInteractiveHighlightTarget(
    STATIC_ADCLONE_PRODUCT_AI_ADD_TARGET,
    STATIC_ADCLONE_TARGET_TIMEOUT_MS,
  );
  if (!ctx.isCurrentRun()) return;
  if (!addBtn) {
    ctx.revealNextGate();
    return;
  }
  enqueueCursorMove({
    target: STATIC_ADCLONE_PRODUCT_AI_ADD_TARGET,
    comment: ONBOARDING_STATIC_ADCLONE_AI_ADD_COPY,
    pulses: 5,
    source: "onboarding",
  });
}

/**
 * After product-created fires, queue the cursor to the footer search CTA.
 * Its arrival handler clicks the button, which starts the Meta/Apify
 * discovery scraper, then waits for the running-ads section.
 */
export async function runStaticAdcloneDiscoveryStep(
  ctx: StaticAdcloneChoreoContext,
): Promise<void> {
  if (!ctx.isCurrentRun()) return;
  ctx.setSpotlightTarget((current) =>
    current === STATIC_ADCLONE_PRODUCT_AI_ADD_TARGET ? null : current,
  );
  const discoverBtn = await waitForInteractiveHighlightTarget(
    STATIC_ADCLONE_DISCOVER_BUTTON_TARGET,
    60_000,
  );
  if (!ctx.isCurrentRun()) return;
  if (!discoverBtn) {
    ctx.revealNextGate();
    return;
  }
  enqueueCursorMove({
    target: STATIC_ADCLONE_DISCOVER_BUTTON_TARGET,
    comment: ONBOARDING_STATIC_ADCLONE_SEARCH_COPY,
    pulses: 5,
    source: "onboarding",
  });
}
